import { memo, type Ref } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentActionsMenuBackRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuBackRow';
import { WorkspaceAgentActionsMenuScrollBody } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuScrollBody';
import { WorkspaceAgentActionsMenuSearchField } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuSearchField';
import { WorkspaceAgentActionsMenuSkillsList } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuSkillsList';
import { type WorkspaceAgentActionsMenuPanelSessionProps } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/workspaceAgentActionsMenuPanelSession';
import { WORKSPACE_AGENT_ACTIONS_MENU_SAVED_SKILLS_LABEL } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuLayout';

export type WorkspaceAgentActionsMenuPanelProps = {
  panelProps: WorkspaceAgentActionsMenuPanelSessionProps;
  searchInputRef: Ref<HTMLInputElement>;
};

export const WorkspaceAgentActionsMenuPanel = memo(
  ({ panelProps, searchInputRef }: WorkspaceAgentActionsMenuPanelProps) => {
    const {
      fillsActionsMenuPopoverHeight,
      handleBack,
      handleBackFromSkills,
      handleQueryChange,
      isSearching,
      isSkillsSubview,
      query,
      searchPlaceholder,
      selectedGroup,
    } = panelProps;

    const showSavedSkillsBackRow = isSkillsSubview;
    const backLabel = showSavedSkillsBackRow ? WORKSPACE_AGENT_ACTIONS_MENU_SAVED_SKILLS_LABEL : selectedGroup?.label;
    const onBack = showSavedSkillsBackRow ? handleBackFromSkills : handleBack;
    const showBackRow = showSavedSkillsBackRow || Boolean(selectedGroup);
    const isBackRowVisible = showBackRow && Boolean(backLabel);

    return (
      <div
        className={cn('flex min-h-0 flex-col overflow-hidden', fillsActionsMenuPopoverHeight ? 'flex-1' : 'shrink-0')}
      >
        <WorkspaceAgentActionsMenuSearchField
          inputRef={searchInputRef}
          onQueryChange={handleQueryChange}
          placeholder={searchPlaceholder}
          query={query}
        />
        {showBackRow && backLabel ? <WorkspaceAgentActionsMenuBackRow label={backLabel} onBack={onBack} /> : null}
        {isSkillsSubview && !isSearching ? (
          <WorkspaceAgentActionsMenuSkillsList
            handleSelectSkill={panelProps.handleSelectSkill}
            hasSkillsListNextPage={panelProps.hasSkillsListNextPage}
            isSkillsListFetchNextPageError={panelProps.isSkillsListFetchNextPageError}
            isSkillsListFetchingNextPage={panelProps.isSkillsListFetchingNextPage}
            isSkillsListLoading={panelProps.isSkillsListLoading}
            requestSkillsListNextPage={panelProps.requestSkillsListNextPage}
            retrySkillsListNextPage={panelProps.retrySkillsListNextPage}
            showSkillsEmpty={panelProps.showSkillsEmpty}
            visibleSkills={panelProps.visibleSkills}
          />
        ) : (
          <WorkspaceAgentActionsMenuScrollBody
            addReferenceLabel={panelProps.addReferenceLabel}
            fillsActionsMenuPopoverHeight={panelProps.fillsActionsMenuPopoverHeight}
            handleAddReference={panelProps.handleAddReference}
            handleOpenSavedSkills={panelProps.handleOpenSavedSkills}
            handleSelectGroup={panelProps.handleSelectGroup}
            handleSelectSkill={panelProps.handleSelectSkill}
            handleSelectTask={panelProps.handleSelectTask}
            isAddReferenceVisible={panelProps.isAddReferenceVisible}
            isBackRowVisible={isBackRowVisible}
            isBlendedSkillsSearchLoading={panelProps.isBlendedSkillsSearchLoading}
            isSavedSkillsEntryVisible={panelProps.isSavedSkillsEntryVisible}
            isSearching={panelProps.isSearching}
            searchPlaceholder={panelProps.searchPlaceholder}
            showEmpty={panelProps.showEmpty}
            visibleBlendedSearchSkills={panelProps.visibleBlendedSearchSkills}
            visibleGroups={panelProps.visibleGroups}
            visibleTasks={panelProps.visibleTasks}
          />
        )}
      </div>
    );
  },
);

WorkspaceAgentActionsMenuPanel.displayName = 'WorkspaceAgentActionsMenuPanel';
